import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import {
  useBillingSummary,
  useStartTopup,
  useUsage,
} from '@/hooks/useBillingQueries';
import { formatAud } from '@/utils/money-utils';
import { SettingsPage, SettingsSection, SettingsSectionBody, SettingsStateLine } from './SettingsPage';

const TYPE_LABELS: Record<string, string> = {
  NEO_CHAT: 'Neo chat',
  PROCESS_RUN: 'Background processes',
};

export function UsageSection() {
  const usage = useUsage();
  const summary = useBillingSummary();
  const startTopup = useStartTopup();

  const actions = (
    <>
      <Button variant="outline" size="sm" onClick={() => usage.refetch()} disabled={usage.isFetching}>
        Refresh
      </Button>
      <Button size="sm" onClick={() => startTopup.mutate(10)} disabled={startTopup.isPending}>
        Top up credit
      </Button>
    </>
  );

  if (usage.isLoading) {
    return (
      <SettingsPage title="Usage" actions={actions}>
        <SettingsStateLine>Loading usage…</SettingsStateLine>
      </SettingsPage>
    );
  }
  if (usage.isError || !usage.data) {
    return (
      <SettingsPage title="Usage" actions={actions}>
        <SettingsStateLine tone="destructive">Couldn't load usage. Refresh to try again.</SettingsStateLine>
      </SettingsPage>
    );
  }

  const data = usage.data;
  const lowBalance = summary.data?.summary.lowBalance ?? false;
  const maxCalls = data.byType.reduce((m, r) => Math.max(m, r.callCount), 0);
  const maxDaily = data.daily.reduce((m, d) => Math.max(m, d.callCount), 0);

  const stats = [
    { label: 'Balance', value: formatAud(data.balanceAud) },
    { label: 'Spent this period', value: formatAud(data.spentAud) },
    { label: 'Calls', value: data.callCount.toLocaleString() },
    { label: 'Tokens', value: data.totalTokens.toLocaleString() },
  ];

  return (
    <SettingsPage
      title="Usage"
      description={`${new Date(data.fromIso).toLocaleDateString()} – ${new Date(data.toIso).toLocaleDateString()} · Updated ${new Date(usage.dataUpdatedAt).toLocaleTimeString()}`}
      actions={actions}
    >
      <div className="grid grid-cols-2 border-b sm:grid-cols-4">
        {stats.map((stat) => (
          <div key={stat.label} className="px-4 py-3">
            <div className="text-xs text-muted-foreground">{stat.label}</div>
            <div className="flex items-center gap-2">
              <span className="text-lg font-medium">{stat.value}</span>
              {stat.label === 'Balance' && lowBalance ? <Badge variant="warning">Low balance</Badge> : null}
            </div>
          </div>
        ))}
      </div>

      <SettingsSection title="By type">
        {data.byType.length === 0 ? (
          <SettingsStateLine>No usage in this period.</SettingsStateLine>
        ) : (
          <SettingsSectionBody className="space-y-2">
            {data.byType.map((row) => (
              <div key={row.apexType} className="flex flex-col gap-1">
                <div className="flex justify-between text-sm">
                  <span>{TYPE_LABELS[row.apexType] ?? row.apexType}</span>
                  <span className="text-muted-foreground">{row.callCount.toLocaleString()} calls</span>
                </div>
                <div className="h-2 w-full rounded bg-muted">
                  <div
                    className="h-2 rounded bg-primary"
                    style={{ width: `${maxCalls > 0 ? (row.callCount / maxCalls) * 100 : 0}%` }}
                  />
                </div>
              </div>
            ))}
          </SettingsSectionBody>
        )}
      </SettingsSection>

      <SettingsSection title="Daily">
        {data.daily.length === 0 ? (
          <SettingsStateLine>No daily usage.</SettingsStateLine>
        ) : (
          <SettingsSectionBody>
            <div className="flex h-24 items-end gap-1">
              {data.daily.map((d) => (
                <div
                  key={d.dateIso}
                  className="flex-1 rounded-t bg-primary"
                  style={{ height: `${maxDaily > 0 ? (d.callCount / maxDaily) * 100 : 0}%` }}
                  title={`${new Date(d.dateIso).toLocaleDateString()}: ${d.callCount} calls`}
                />
              ))}
            </div>
          </SettingsSectionBody>
        )}
      </SettingsSection>
    </SettingsPage>
  );
}
